import { BadgeGroup, Flag } from '@src/components';
import {
  Package,
  PackageSummary,
} from '@src/services/packages/packageApiSliceTypes';
import { cleanNumberDisplay, getCurrencySymbol } from '@src/utilities/currency';
import clsx from 'clsx';

interface PackageCardProps {
  item: PackageSummary | Package;
  country?: { name?: string; flag?: string };
  showPrice?: boolean;
  className?: string;
  showFlag?: boolean;
}

export function PackageCard({
  item,
  country,
  showPrice = true,
  className,
  showFlag = true,
}: Readonly<PackageCardProps>) {
  const badges: { label: string; bgColor: string }[] = [];

  if (item.isBestSeller) {
    badges.push({ label: 'BEST SELLER', bgColor: 'bg-wl-primary' });
  }

  if (item.isPopular) {
    badges.push({ label: 'Popular', bgColor: 'bg-green-500' });
  }

  if (item.discountPercentage > 0) {
    badges.push({
      label: `${item.discountPercentage}% Discount`,
      bgColor: 'bg-red-500',
    });
  }

  return (
    <div
      className={clsx(
        'relative flex flex-col items-center justify-between p-4 rounded-xl bg-wl-background',
        className
      )}
    >
      {badges && <BadgeGroup badges={badges} />}
      <div className='w-full flex justify-between'>
        <div className='flex space-x-3 items-center'>
          <div className='flex flex-col items-center justify-center font-bold text-wl-primary bg-wl-primary-container w-12 h-12 px-8 rounded-md'>
            {cleanNumberDisplay(item.dataLimit)}
            {item.dataLimitUnitType}
          </div>

          <div className='flex flex-col text-wl-on-surface text-left text-B-14'>
            <span className='text-B-16 leading-snug break-words line-clamp-2'>
              {item.name}
            </span>
            <span className='sm:text-B-14 text-[11px] md:font-medium text-wl-on-background opacity-50'>
              {`${item.expireIn}day${country?.name ? ' / ' + country.name : ''}`}
            </span>
          </div>
        </div>
        <div className='flex items-center font-bold [&>*]:text-B-20 text-wl-on-surface space-x-4'>
          {showPrice &&
            (item.discountPercentage > 0 ? (
              <div className='flex gap-4 items-center'>
                <div className='text-sm text-gray-600 dark:text-wl-secondary line-through'>
                  {getCurrencySymbol(item.currency)}
                  {cleanNumberDisplay(item.price)}
                </div>
                <div className='text-wl-on-background'>
                  {getCurrencySymbol(item.currency)}
                  {item.discountedPrice}
                </div>
              </div>
            ) : (
              <div className='text-wl-on-background'>
                {getCurrencySymbol(item.currency)}
                {cleanNumberDisplay(item.price)}
              </div>
            ))}
          {showFlag && country?.flag && <Flag flagSrc={country.flag} />}
        </div>
      </div>
    </div>
  );
}

export default PackageCard;
